import { useInfiniteQuery, useQuery } from 'react-query';
import { errorToast } from 'lib/components/toasts/error';
import { EXPRESS_API } from 'configs/api/expressApi';
import { landingPageDesignsKeys } from './landingPageDesignsKeys';
import { Customer } from 'lib/context';
import { LP_DESIGN_CATEGORIES } from 'app/pages/admin/landing-pages-v2/constants';
import { ILandingPageDesignResponse } from 'app/pages/admin/landing-pages-v2/types';

interface Reseller {
  resellerId: number;
  resellerName: string;
}

export interface LandingPageDesignAccess {
  id: number;
  landingPageDesignId: number;
  reseller: Reseller;
  customer: Customer;
}

export interface ILandingPageDesignsResponse {
  count: 0;
  data: ILandingPageDesignResponse[];
}

export interface ILandingPageDesignsQueryParams {
  limit: number;
  search: string;
  start?: number;
  category?: LP_DESIGN_CATEGORIES | null;
  resellerIds?: number[];
  groupIds?: number[];
  active?: boolean | null;
}

const normalizeLandingPageDesignsParams = (
  params: ILandingPageDesignsQueryParams
): Required<ILandingPageDesignsQueryParams> => ({
  limit: params.limit,
  start: params.start ?? 0,
  search: params.search ?? '',
  category: params.category ?? null,
  resellerIds: params.resellerIds ?? [],
  groupIds: params.groupIds ?? [],
  active: params.active ?? null,
});

const getLandingPageDesigns = async (
  params: ILandingPageDesignsQueryParams
): Promise<ILandingPageDesignsResponse> => {
  const normalizedParams = normalizeLandingPageDesignsParams(params);
  return (
    await EXPRESS_API.get(`/superadmin/landing-page-designs`, {
      params: normalizedParams,
    })
  ).data;
};

export const useQueryLandingPageDesigns = (
  params: ILandingPageDesignsQueryParams,
  enabled: boolean = true
) => {
  const normalizedParams = normalizeLandingPageDesignsParams(params);
  return useQuery(
    landingPageDesignsKeys.list(normalizedParams),
    () => getLandingPageDesigns(normalizedParams),
    {
      refetchOnMount: true,
      staleTime: 0,
      enabled,
      onError: () => {
        errorToast({
          title:
            "We're currently experiencing difficulties fetching landing page designs",
        });
      },
    }
  );
};

export const useInfiniteQueryLandingPageDesigns = (
  params: ILandingPageDesignsQueryParams,
  enabled: boolean = true
) => {
  const normalizedParams = normalizeLandingPageDesignsParams(params);
  return useInfiniteQuery(
    landingPageDesignsKeys.infinite_landing_page_designs(normalizedParams),
    ({ pageParam = 0 }) =>
      getLandingPageDesigns({
        ...normalizedParams,
        start: pageParam * normalizedParams.limit,
      }),
    {
      refetchOnMount: true,
      getNextPageParam: (_, allPages) => {
        return allPages.length;
      },
      onError: () => {
        errorToast({
          title:
            "We're currently experiencing difficulties fetching landing page designs",
        });
      },
      enabled,
    }
  );
};
